import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";

const STAGES = [
    { key: "submitted", label: "Submitted" },
    { key: "screening", label: "Screening" },
    { key: "interview", label: "Interview" },
    { key: "offer", label: "Offer" },
    { key: "placed", label: "Placed" },
    { key: "rejected", label: "Rejected" },
];

export default function Dashboard() {
    const [subs, setSubs] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);

    async function refresh() {
        const [s, t] = await Promise.all([
            api.get("/submissions").then((r) => r.data),
            api.get("/dashboard/stats").then((r) => r.data),
        ]);
        setSubs(s);
        setStats(t);
    }

    useEffect(() => {
        refresh().finally(() => setLoading(false));
    }, []);

    async function moveStage(sub, nextStage) {
        const prev = sub.stage;
        // optimistic
        setSubs((cur) => cur.map((x) => (x.id === sub.id ? { ...x, stage: nextStage } : x)));
        try {
            await api.patch(`/submissions/${sub.id}/stage`, { stage: nextStage });
            toast.success(`Moved to ${nextStage}`);
            const t = await api.get("/dashboard/stats").then((r) => r.data);
            setStats(t);
        } catch (e) {
            setSubs((cur) => cur.map((x) => (x.id === sub.id ? { ...x, stage: prev } : x)));
            toast.error("Could not update stage");
        }
    }

    if (loading) return <div className="py-24 text-center text-[#A3A3A3]">Loading dashboard…</div>;

    return (
        <div data-testid="dashboard-page" className="mx-auto max-w-[1600px] px-6 py-12 md:px-10">
            <div className="mb-10 flex flex-wrap items-end justify-between gap-6">
                <div>
                    <div className="caption text-[var(--brand-accent)]">Recruiter Admin</div>
                    <h1 className="mt-3 font-serif text-5xl tracking-display text-[#FAFAFA]">Pipeline</h1>
                </div>
                <a
                    href="/checkout"
                    data-testid="cta-checkout"
                    className="inline-flex items-center justify-center gap-2 rounded-sm px-6 py-3 text-sm font-medium transition-all duration-300 hover:shadow-[0_0_24px_rgba(201,169,110,0.35)]"
                    style={{ background: "var(--brand-accent)", color: "#0A0A0A" }}
                >
                    Pay $250 Boarding Fee →
                </a>
            </div>

            {/* Stats */}
            <div className="mb-10 grid gap-px bg-white/5 sm:grid-cols-2 md:grid-cols-4">
                <Stat label="Total Submissions" value={stats?.total_submissions ?? 0} testId="stat-total" />
                <Stat label="Bounty Pending" value={`$${((stats?.bounty_pending ?? 0) / 1000).toFixed(0)}k`} testId="stat-pending" />
                <Stat label="Bounty Earned" value={`$${((stats?.bounty_earned ?? 0) / 1000).toFixed(0)}k`} testId="stat-earned" highlight />
                <Stat label="Placed" value={stats?.by_stage?.placed ?? 0} testId="stat-placed" />
            </div>

            {/* Kanban */}
            <div className="flex gap-4 overflow-x-auto pb-6" data-testid="kanban-board">
                {STAGES.map((stage) => {
                    const cards = subs.filter((s) => s.stage === stage.key);
                    return (
                        <div
                            key={stage.key}
                            data-testid={`kanban-column-${stage.key}`}
                            className="flex h-full w-80 flex-shrink-0 flex-col gap-4 rounded-md border border-white/5 bg-[#0A0A0A]/50 p-4"
                        >
                            <div className="flex items-center justify-between">
                                <div className="caption text-[#A3A3A3]">{stage.label}</div>
                                <span className="font-mono text-xs text-[#737373]">{cards.length}</span>
                            </div>
                            <div className="flex flex-col gap-3">
                                {cards.length === 0 && (
                                    <div className="rounded-sm border border-dashed border-white/5 p-6 text-center text-xs text-[#737373]">
                                        Empty
                                    </div>
                                )}
                                {cards.map((s) => (
                                    <KanbanCard
                                        key={s.id}
                                        sub={s}
                                        onMove={(next) => moveStage(s, next)}
                                    />
                                ))}
                            </div>
                        </div>
                    );
                })}
            </div>

            {subs.length === 0 && (
                <div className="mt-12 rounded-md border border-white/5 bg-[#141414]/60 p-12 text-center">
                    <div className="caption text-[var(--brand-accent)]">No submissions yet</div>
                    <h3 className="mt-3 font-serif text-3xl tracking-display text-[#FAFAFA]">Submit your first candidate</h3>
                    <p className="mt-3 text-sm text-[#A3A3A3]">Browse the Jobs Feed and submit a candidate to a mandate to start earning.</p>
                </div>
            )}
        </div>
    );
}

function Stat({ label, value, highlight, testId }) {
    return (
        <div data-testid={testId} className="bg-[#0A0A0A] p-6">
            <div className="caption text-[#737373]">{label}</div>
            <div
                className={`mt-3 font-serif text-4xl tracking-display ${highlight ? "" : "text-[#FAFAFA]"}`}
                style={highlight ? { color: "var(--brand-accent)" } : undefined}
            >
                {value}
            </div>
        </div>
    );
}

function KanbanCard({ sub, onMove }) {
    const score = sub.match?.overall_score ?? 0;
    return (
        <div
            data-testid={`kanban-card-${sub.id}`}
            className="rounded-sm border border-white/10 bg-[#141414] p-4 transition-all duration-300 hover:-translate-y-0.5 hover:border-[var(--brand-accent)] hover:shadow-[0_4px_20px_rgba(201,169,110,0.12)]"
        >
            <div className="flex items-start justify-between gap-3">
                <div>
                    <div className="font-medium text-sm text-[#FAFAFA]">{sub.candidate_name}</div>
                    <div className="text-xs text-[#A3A3A3] mt-0.5">{sub.candidate_title}</div>
                </div>
                <div
                    className="flex h-10 w-10 flex-col items-center justify-center rounded-full border text-xs"
                    style={{
                        borderColor: "rgba(201,169,110,0.4)",
                        background: "rgba(201,169,110,0.1)",
                        color: "var(--brand-accent)",
                    }}
                >
                    <span className="font-serif text-base leading-none">{score}</span>
                </div>
            </div>
            <div className="mt-3 caption text-[#737373]">{sub.company} · {sub.job_title}</div>
            <div className="mt-2 caption" style={{ color: "var(--brand-accent)" }}>
                ${(sub.bounty / 1000).toFixed(0)}k bounty
            </div>
            <select
                data-testid={`select-stage-${sub.id}`}
                value={sub.stage}
                onChange={(e) => onMove(e.target.value)}
                className="mt-3 w-full rounded-sm border border-white/10 bg-black/40 px-2 py-1.5 text-xs text-[#FAFAFA] focus:border-[var(--brand-accent)] focus:outline-none"
            >
                {STAGES.map((s) => (
                    <option key={s.key} value={s.key}>{s.label}</option>
                ))}
            </select>
        </div>
    );
}
